import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  TfColumnDefDirective,
  TfDataTableComponent,
} from '@tourfold/libraries/shared-ui';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { MeasureUnitResponse } from '@tourfold/libraries/tf-api-client-public-v2';
import { MeasureUnitsService } from './measure-units.service';
import { MeasureUnitDialog, MeasureUnitFormResult } from './measure-unit-dialog';

@Component({
  selector: 'tf-settings-measure-units-list',
  imports: [
    ButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    TranslateModule,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    TfDataTableComponent,
    TfColumnDefDirective,
    SettingsInlineMessage,
    SettingsLoadError,
    MeasureUnitDialog,
  ],
  templateUrl: './measure-units-list.html',
  styleUrl: './measure-units-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class MeasureUnitsList {
  private readonly measureUnitsService = inject(MeasureUnitsService);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly dialog = viewChild(MeasureUnitDialog);

  readonly canCreate = computed(() => this.resourceAccessService.canPerform('capacity', 'create'));
  readonly canUpdate = computed(() => this.resourceAccessService.canPerform('capacity', 'update'));
  readonly canDelete = computed(() => this.resourceAccessService.canPerform('capacity', 'delete'));

  readonly units = signal<MeasureUnitResponse[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly searchQuery = signal<string>('');
  readonly dialogVisible = signal<boolean>(false);
  readonly editTarget = signal<MeasureUnitResponse | null>(null);
  readonly pageSize = 10;

  readonly columns: ColumnDef[] = [
    { field: 'name', title: 'SETTINGS_MEASURE_UNITS.NAME', type: 'text', sortable: true, width: '50%' },
    {
      field: 'symbol',
      title: 'SETTINGS_MEASURE_UNITS.SYMBOL',
      type: 'text',
      sortable: true,
      width: '30%',
    },
    { field: '_actions', title: 'SETTINGS_MEASURE_UNITS.ACTIONS', type: 'custom' },
  ];

  readonly filteredUnits = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (query === '') return this.units();
    return this.units().filter(
      (unit) =>
        (unit.name ?? '').toLowerCase().includes(query) ||
        (unit.symbol ?? '').toLowerCase().includes(query)
    );
  });

  readonly showEmptyState = computed(
    () => this.hasLoadedOnce() && this.searchQuery().trim() === '' && this.units().length === 0
  );

  constructor() {
    this.loadUnits();
  }

  private loadUnits(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);

    this.measureUnitsService.getMeasureUnits().subscribe({
      next: (units) => {
        this.units.set(units);
        this.isLoading.set(false);
        this.loadError.set(null);
        this.loadErrorDetails.set(null);
        this.initialLoadFailed.set(false);
        this.hasLoadedOnce.set(true);
      },
      error: (error: unknown) => {
        this.isLoading.set(false);
        const message = this.translateService.instant('SETTINGS.LOAD_ERROR.TITLE');
        if (hadLoadedOnce) {
          this.hasLoadedOnce.set(true);
          this.actionError.set(message);
        } else {
          this.loadError.set(message);
          this.initialLoadFailed.set(true);
          void this.updateLoadErrorDetails(error);
        }
      },
    });
  }

  reload(): void {
    this.loadUnits();
  }

  onSearchChange(query: string): void {
    this.searchQuery.set(query);
  }

  openCreate(): void {
    this.editTarget.set(null);
    this.dialogVisible.set(true);
  }

  openEdit(unit: MeasureUnitResponse): void {
    this.editTarget.set(unit);
    this.dialogVisible.set(true);
  }

  closeDialog(): void {
    this.dialogVisible.set(false);
    this.editTarget.set(null);
  }

  async onDialogSave(result: MeasureUnitFormResult): Promise<void> {
    const target = this.editTarget();
    this.isSaving.set(true);

    const request$ = target
      ? this.measureUnitsService.updateMeasureUnit(target.id ?? '', {
          name: result.name,
          symbol: result.symbol,
          lockVersion: target.lockVersion ?? 0,
        })
      : this.measureUnitsService.createMeasureUnit({
          name: result.name,
          symbol: result.symbol,
        });

    try {
      await firstValueFrom(request$);
      this.closeDialog();
      this.loadUnits();
    } catch (error: unknown) {
      // Keep the dialog open and surface the error inside it — inline on the field when the
      // server maps it, otherwise a banner. The list's actionError is for non-dialog ops.
      await this.dialog()?.applyServerError(error);
    } finally {
      this.isSaving.set(false);
    }
  }

  deleteUnit(unit: MeasureUnitResponse): void {
    const message = this.translateService
      .instant('SETTINGS_MEASURE_UNITS.DELETE_CONFIRM_MESSAGE')
      .replace('{name}', unit.name ?? '');

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_MEASURE_UNITS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.measureUnitsService.deleteMeasureUnit(unit.id ?? '').subscribe({
          next: () => {
            this.isLoading.set(false);
            this.loadUnits();
          },
          error: () => {
            this.isLoading.set(false);
            this.actionError.set(
              this.translateService.instant('SETTINGS_MEASURE_UNITS.ERROR.DELETE_FAILED')
            );
          },
        });
      },
    });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }
}
